/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { Badge, Flex, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs, Text, Tooltip } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import c from '../../../constant';
import useStore from '../../../store';
import ReportArchivedLayout from '../ReportArchivedLayout';
import ReportMenuItem from '../ReportMenuItem';

const ews = 'ews';
const ewsgi = 'ewsCanViewStatementGeneralInsurance';
const ewsli = 'ewsCanViewStatementLifeInsurance';
const ewsmi = 'ewsCanViewStatementMicroInsurance';
const ewsri = 'ewsCanViewStatementReinsurance';

const mdd = 'mdd';
const mddgmr = 'mddgmr';
const mddlmr = 'mddlmr';
const mddmmr = 'mddmmr';
const mmdrd = 'mmdrd';
const mmdcd = 'mmdcd';
const periods = ['monthly', 'quarterly', 'semesterly', 'annually'];

const QUERY_COMPANY_REPORT_TYPE = gql`
	query get($period: String!) {
		getPublicCompanyReportTypes(period: $period) {
			id
			title
			titleKh
			period
			departments
		}
	}
`;

export default function ReportLayout({ period = null }) {
	const { replace } = useHistory();
	const { t } = useTranslation('common');
	const [loadReportTypeByPeriod, { data, loading }] = useLazyQuery(QUERY_COMPANY_REPORT_TYPE);
	const { unreadReportCount } = useStore((state) => state.unreadReportCount);
	const setUnreadReportCount = useStore((state) => state.setUnreadReportCount);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const [initialReportTypes, setInitialReportTypes] = useState([]);
	const [reportTypes, setReportTypes] = useState(null);

	const roles = useMemo(() => user?.role.split(',') || [], [user]);

	const isGiDataUser = useMemo(() => {
		if (roles?.includes('admin')) return true;
		return roles?.includes(mdd) && user?.mdd?.includes(mddgmr) && user?.mdd?.includes(mmdrd);
	}, [roles]);

	const isLiDataUser = useMemo(() => {
		if (roles?.includes('admin')) return true;
		return roles?.includes(mdd) && user?.mdd?.includes(mddlmr) && user?.mdd?.includes(mmdrd);
	}, [roles]);

	const isMiDataUser = useMemo(() => {
		if (roles?.includes('admin')) return true;
		return roles?.includes(mdd) && user?.mdd?.includes(mddmmr) && user?.mdd?.includes(mmdrd);
	}, [roles]);

	const isGiConUser = useMemo(() => {
		if (roles?.includes('admin')) return true;
		return roles?.includes(mdd) && user?.mdd?.includes(mddgmr) && user?.mdd?.includes(mmdcd);
	}, [roles]);

	const isLiConUser = useMemo(() => {
		if (roles?.includes('admin')) return true;
		return roles?.includes(mdd) && user?.mdd?.includes(mddlmr) && user?.mdd?.includes(mmdcd);
	}, [roles]);

	const isMiConUser = useMemo(() => {
		if (roles?.includes('admin')) return true;
		return roles?.includes(mdd) && user?.mdd?.includes(mddmmr) && user?.mdd?.includes(mmdcd);
	}, [roles]);

	const tabIndex = useMemo(() => Math.max(0, periods.indexOf(period)), [period]);

	const onTabChange = (index) => {
		replace(`?period=${periods[index]}`);
	};

	useEffect(() => {
		if (user) {
			const localInitialReportTypes = [];
			/**
			 * MD
			 */
			if (isGiDataUser || isLiDataUser || isMiDataUser || isGiConUser || isLiConUser || isMiConUser) {
				localInitialReportTypes.push({
					id: c.monthly_sale_report,
					title: 'Sale Report for Insurance Company',
					titleKh: 'របាយការណ៍អាជីវកម្មរបស់ក្រុមហ៊ុនធានារ៉ាប់រង',
					period: 'MONTHLY',
					path: '/market-development-data?period=monthly&reportId=' + c.monthly_sale_report,
					hint: getHintMdd(user?.mdd),
				});
			}
			if (isGiDataUser) {
				localInitialReportTypes.push({
					id: c.quarterly_sale_report,
					title: 'Sale Report for Insurance Company',
					titleKh: 'របាយការណ៍អាជីវកម្មរបស់ក្រុមហ៊ុនធានារ៉ាប់រង',
					period: 'QUARTERLY',
					path: '/market-development-data?period=quarterly&reportId=' + c.quarterly_sale_report,
					hint: '(GI)',
				});
			}
			if (isGiDataUser || isLiDataUser || isMiDataUser) {
				localInitialReportTypes.push({
					id: c.monthly_claim_report,
					title: 'Receiving and Settling Claim Report for Insurance Company',
					titleKh: 'របាយការណ៍ទទួល និងដោះស្រាយសំណងរបស់ក្រុមហ៊ុនធានារ៉ាប់រង',
					period: 'MONTHLY',
					path: '/market-development-data?period=monthly&reportId=' + c.monthly_claim_report,
					hint: getHintMdd(user?.mdd),
				});
				localInitialReportTypes.push({
					id: c.monthly_distribution_channel_report,
					title: 'Distribution Channels and Geographical Report for Insurance Company',
					titleKh: 'របាយការណ៍លក់តាមប្រព័ន្ធចែកចាយ និងតាមភូមិសាស្ត្ររបស់ក្រុមហ៊ុនធានារ៉ាប់រង',
					period: 'MONTHLY',
					path: '/market-development-data?period=monthly&reportId=' + c.monthly_distribution_channel_report,
					hint: getHintMdd(user?.mdd),
				});
				localInitialReportTypes.push({
					id: c.quarterly_claim_report,
					title: 'Receiving and Settling Claim Report for Insurance Company',
					titleKh: 'របាយការណ៍អំពីការទទួល និងដោះស្រាយសំណងធានារ៉ាប់រង',
					period: 'QUARTERLY',
					path: '/market-development-data?period=quarterly&reportId=' + c.quarterly_claim_report,
					hint: getHintMdd(user?.mdd),
				});
				localInitialReportTypes.push({
					id: c.annually_sale_report,
					title: 'Audited Sale Report for Insurance Company',
					titleKh: 'របាយការណ៍អាជីវកម្មរបស់ក្រុមហ៊ុនធានារ៉ាប់រងរងសវនកម្ម',
					period: 'ANNUALLY',
					path: '/market-development-data?period=annually&reportId=' + c.annually_sale_report,
					hint: getHintMdd(user?.mdd),
				});
				localInitialReportTypes.push({
					id: c.annually_claim_report,
					title: 'Audited Receiving and Settling Claim Report for Insurance Company',
					titleKh: 'របាយការណ៍ទទួល និងដោះស្រាយសំណងរបស់ក្រុមហ៊ុនធានារ៉ាប់រងរងសវនកម្ម',
					period: 'ANNUALLY',
					path: '/market-development-data?period=annually&reportId=' + c.annually_claim_report,
					hint: getHintMdd(user?.mdd),
				});
				localInitialReportTypes.push({
					id: c.annually_distribution_channel_report,
					title: 'Audited Distribution Channels and Geographical Report for Insurance Company',
					titleKh: 'របាយការណ៍លក់តាមប្រព័ន្ធចែកចាយ និងតាមភូមិសាស្ត្ររបស់ក្រុមហ៊ុនធានារ៉ាប់រងរងសវនកម្ម',
					period: 'ANNUALLY',
					path: '/market-development-data?period=annually&reportId=' + c.annually_distribution_channel_report,
					hint: getHintMdd(user?.mdd),
				});
			}
			/**
			 * SD
			 */
			const roles = user?.role.split(',') || [];
			if (roles?.includes('admin') || roles?.includes(ews)) {
				localInitialReportTypes.push({
					id: c.quarterly_financial_statements,
					title: 'Financial Statements for Insurance Company',
					titleKh: 'របាយការណ៍ហិរញ្ញវត្ថុរបស់ក្រុមហ៊ុនធានារ៉ាប់រង',
					period: 'QUARTERLY',
					path: '/early-warning-system?period=quarterly&reportId=' + c.quarterly_financial_statements + '&page=1&report=ews',
					hint: getHintEws(user?.ews),
				});
				localInitialReportTypes.push({
					id: c.quarterly_report_on_investment,
					title: 'Report on Investment for Insurance Company',
					titleKh: 'របាយការណ៍វិនិយោគរបស់ក្រុមហ៊ុនធានារ៉ាប់រង',
					period: 'QUARTERLY',
					path: '/early-warning-system?period=quarterly&reportId=' + c.quarterly_report_on_investment + '&page=1&report=ir',
					hint: getHintEws(user?.ews),
				});
				localInitialReportTypes.push({
					id: c.annually_audited_financial_statements,
					title: 'Audited Financial Statement for Insurance Company',
					titleKh: 'របាយការណ៍ហិរញ្ញវត្ថុប្រចាំឆ្នាំរបស់ក្រុមហ៊ុនធានារ៉ាប់រងរងសវនកម្ម',
					period: 'ANNUALLY',
					path: '/early-warning-system?period=annually&reportId=' + c.annually_audited_financial_statements + '&page=1&report=ews',
					hint: getHintEws(user?.ews),
				});
				localInitialReportTypes.push({
					id: c.annually_unaudited_financial_statements,
					title: 'Unaudited Financial Statement for Insurance Company',
					titleKh: 'របាយការណ៍ហិរញ្ញវត្ថុប្រចាំឆ្នាំរបស់ក្រុមហ៊ុនធានារ៉ាប់រងមិនទាន់រងសវនកម្ម',
					period: 'ANNUALLY',
					path: '/early-warning-system?period=annually&reportId=' + c.annually_unaudited_financial_statements + '&page=1&report=ews',
					hint: getHintEws(user?.ews),
				});
				localInitialReportTypes.push({
					id: c.annually_report_on_investment,
					title: 'Audited Report on Investment for Insurance Company',
					titleKh: 'របាយការណ៍វិនិយោគរបស់ក្រុមហ៊ុនធានារ៉ាប់រងរងសវនកម្ម',
					period: 'ANNUALLY',
					path: '/early-warning-system?period=annually&reportId=' + c.annually_report_on_investment + '&page=1&report=ir',
					hint: getHintEws(user?.ews),
				});
			}
			setInitialReportTypes(localInitialReportTypes);
		}
	}, [user]);

	useEffect(() => loadReportTypeByPeriod({ variables: { period: '' } }), [initialReportTypes]);

	useEffect(() => {
		if (data?.getPublicCompanyReportTypes) {
			const tempReport = [...initialReportTypes];
			data?.getPublicCompanyReportTypes?.forEach((item, index) => {
				const roles = user?.role.split(',') || [];
				const reportDepartments = item?.departments?.split(',') || [];
				const userDepartments = user?.department?.split(',') || [];
				const status = userDepartments.some((department) => reportDepartments.includes(department));
				if (
					roles?.includes('admin') ||
					status ||
					(user?.username === 'sngounsreynoch' && item?.period === 'MONTHLY') // static user permission
				) {
					tempReport.push({
						id: item?.id,
						title: item?.title,
						titleKh: item?.titleKh,
						period: item?.period,
						path: `/company-submission-report/report?type=${item?.period?.toLowerCase()}&page=1&newReportType=${item?.id}&title=${item?.title}&titleKh=${item?.titleKh}`,
					});
				}
			});
			setReportTypes(tempReport);
		}
	}, [data]);

	function callbackCountUnread({ count, reportId, period }) {
		const temp = { ...unreadReportCount, [reportId]: { count, period } };
		setUnreadReportCount(temp);
	}

	function getCountUnread(listCount, item) {
		let count = 0;
		for (var k in listCount) {
			if (listCount.hasOwnProperty(k)) {
				if (listCount[k]?.period === item) {
					count = count + listCount[k]?.count;
				}
			}
		}
		return count;
	}

	function getHintMdd(permissions) {
		let hints = [];
		let roles = user?.role.split(',') || [];
		if (roles?.includes('admin')) {
			hints.push('GI');
			hints.push('LI');
			hints.push('MI');
		} else {
			if (permissions?.includes(mddgmr)) {
				hints.push('GI');
			}
			if (permissions?.includes(mddlmr)) {
				hints.push('LI');
			}
			if (permissions?.includes(mddmmr)) {
				hints.push('MI');
			}
		}
		return '(' + [hints?.slice(0, -1)?.join(' '), hints?.slice(-1)[0]].join(hints?.length < 2 ? '' : ' & ') + ')';
	}

	function getHintEws(permissions) {
		let hints = [];
		let roles = user?.role.split(',') || [];
		if (roles?.includes('admin')) {
			hints.push('GI');
			hints.push('LI');
			hints.push('MI');
			hints.push('RE');
		} else {
			if (permissions?.includes(ewsgi)) {
				hints.push('GI');
			}
			if (permissions?.includes(ewsli)) {
				hints.push('LI');
			}
			if (permissions?.includes(ewsmi)) {
				hints.push('MI');
			}
			if (permissions?.includes(ewsri)) {
				hints.push('RE');
			}
		}
		return '(' + [hints?.slice(0, -1)?.join(' '), hints?.slice(-1)[0]].join(hints?.length < 2 ? '' : ' & ') + ')';
	}

	function ReportList({ reportTypes = [], period = null }) {
		const localReportTypes = useMemo(() => reportTypes?.filter((el) => el?.period?.toLowerCase() === period?.toLowerCase()), [reportTypes]);

		return (
			<>
				{localReportTypes?.length > 0 && (
					<>
						{localReportTypes?.map((el, i) => {
							return (
								<ReportMenuItem
									key={i}
									item={el}
									index={i}
									callbackCountUnread={(e) => callbackCountUnread({ count: e, reportId: el?.id, period })}
								/>
							);
						})}
					</>
				)}
				{localReportTypes?.length === 0 && (
					<Flex
						width="100%"
						alignItems="center"
						bg="gray.100"
						borderRadius={c.borderRadius}
						borderWidth={1}
						borderStyle="solid"
						borderColor="gray.300"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						p="2"
						cursor="pointer"
						mb="2"
					>
						<Text fontWeight="bold" color="gray.400">
							{t('No Data')}
						</Text>
					</Flex>
				)}
			</>
		);
	}

	return (
		<>
			<Flex w="100%" overflowX="scroll">
				<Tabs
					className="hidden-print"
					index={tabIndex}
					bg="#ECECEF"
					variant="enclosed"
					width="100%"
					borderRadius={c.borderRadius}
					border="1px solid #dbdbdb"
					boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
					onChange={onTabChange}
				>
					<TabList>
						{periods?.map((item, index) => (
							<Tab
								key={index}
								fontWeight="bold"
								width="100%"
								borderRadius="0 !important"
								_focus={{ boxShadow: 'none' }}
								_selected={{ color: 'white', bg: 'blue.500' }}
							>
								<Tooltip label={t(item)}>
									<Text color="inherit" noOfLines={1}>
										{t(item)}
									</Text>
								</Tooltip>
								{getCountUnread(unreadReportCount, item) > 0 && (
									<Badge fontSize="12px" ml="1" borderRadius={0} colorScheme="red" left={0}>
										{getCountUnread(unreadReportCount, item)}
									</Badge>
								)}
							</Tab>
						))}
					</TabList>
					<TabPanels>
						{periods?.map((period, index) => (
							<TabPanel p={4} key={index}>
								{loading && (
									<center>
										<Spinner mt={4} mb={4} size="md" thickness={2} color="gray.400" />
									</center>
								)}
								{!loading && reportTypes && (
									<>
										<ReportList reportTypes={reportTypes} period={period} />
									</>
								)}
								<ReportArchivedLayout period={period} />
							</TabPanel>
						))}
					</TabPanels>
				</Tabs>
			</Flex>
		</>
	);
}
